import React, { useContext, useEffect } from 'react';
import { useLocation, matchPath, Link as RouterLink } from "react-router-dom";
import { alpha, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Link as ScrollLink, scroller } from "react-scroll";
import Sitemark from './SitemarkIcon';
import { NoEffectButton } from './NoEffectButton';
import { AppContext } from 'src/AppContext';
import { AppStoreButton } from './AppStoreButton';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

function getSectionFromUrl(url) {
  if (typeof url !== 'string') {
    return "";
  }
  const hashIndex = url.indexOf("#");
  if (hashIndex !== -1 && hashIndex + 1 < url.length) {
    return url.substring(hashIndex + 1);
  }
  return "";
}

const CustomLink = ({ isHomePage = false, href, ...props }) => {
  const handleClick = (event) => {
    if (href === "download") {
      event.preventDefault();
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  if (isHomePage) {
    const section = getSectionFromUrl(href);
    return (
      <ScrollLink
        to={section}
        smooth={true}
        duration={500}
        style={{ textDecoration: "none", color: "inherit" }}
        onClick={handleClick}
      >
        {props.children}
      </ScrollLink>
    );
  }

  return (
    <RouterLink
      to={href}
      style={{ textDecoration: "none", color: "inherit" }}
      onClick={handleClick}
    >
      {props.children}
    </RouterLink>
  );
};

export default function AppAppBar() {
  const { user, loading } = useContext(AppContext);
  const location = useLocation();

  const [open, setOpen] = React.useState(false);

  const isHomePage = matchPath({ path: "/", exact: true }, location.pathname) ? true : false;

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  useEffect(() => {
    const section = getSectionFromUrl(location.hash);
    if (section) {
      scroller.scrollTo(section, {
        smooth: true,
        duration: 500,
      });
    }
  }, [location]);

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <NoEffectButton href='/'>
              <Sitemark />
            </NoEffectButton>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" color="info" size="small">
                <CustomLink isHomePage={isHomePage} href="/#services">Services</CustomLink>
              </Button>
              <Button variant="text" color="info" size="small">
                <CustomLink isHomePage={isHomePage} href="/#trainers">Trainers</CustomLink>
              </Button>
              <Button variant="text" color="info" size="small">
                <CustomLink isHomePage={isHomePage} href="/#pricing">Pricing</CustomLink>
              </Button>
              <Button variant="text" color="info" size="small" sx={{ minWidth: 0 }}>
                <CustomLink isHomePage={isHomePage} href="/#faq">FAQ</CustomLink>
              </Button>
              <Button variant="text" color="info" size="small" sx={{ minWidth: 0 }}>
                <CustomLink isHomePage={isHomePage} href="download">Download</CustomLink>
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
            }}
          >
            {
              user ? <>
                <NoEffectButton href="/me">
                  <Avatar src={user?.avatarURL} />
                </NoEffectButton>
              </> : <>
                {
                  loading ? <></> : <>
                    <Button color="primary" variant="text" size="small" href='/signin'>
                      Sign in
                    </Button>
                    <Button color="secondary" variant="contained" size="small" href='/signup'>
                      Sign up
                    </Button>
                  </>
                }
              </>
            }
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <MenuItem>
                  <CustomLink isHomePage={isHomePage} href="/#services">Services</CustomLink>
                </MenuItem>
                <MenuItem>
                  <CustomLink isHomePage={isHomePage} href="/#trainers">Trainers</CustomLink>
                </MenuItem>
                <MenuItem>
                  <CustomLink isHomePage={isHomePage} href="/#pricing">Pricing</CustomLink>
                </MenuItem>
                <MenuItem>
                  <CustomLink isHomePage={isHomePage} href="/#faq">FAQ</CustomLink>
                </MenuItem>
                <Divider sx={{ my: 3 }} />
                {
                  user ? <>
                    <MenuItem component="a" href='/me'>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Avatar src={user?.avatarURL} />
                        <Typography>{user?.userName}</Typography>
                      </Box>
                    </MenuItem>
                  </> : <>
                    <MenuItem>
                      <Button color="secondary" variant="contained" fullWidth href='/signup'>
                        Sign up
                      </Button>
                    </MenuItem>
                    <MenuItem>
                      <Button color="secondary" variant="outlined" fullWidth href='/signin'>
                        Sign in
                      </Button>
                    </MenuItem>
                  </>
                }
                <Divider sx={{ my: 3 }} />
                <Box sx={{ px: 1 }} >
                  <AppStoreButton direction='column' />
                </Box>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar >
  );
}
